import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Files,
  Package,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Clock,
} from "lucide-react";
import { KPICard } from "@/components/KPICard";
import { UploadZone } from "@/components/UploadZone";
import { TimelineChart } from "@/components/TimelineChart";
import { CategoriasGrid } from "@/components/CategoriasGrid";
import { getStats } from "@/lib/ocorenApi";

const StatsBar = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.total));
  if (items.length === 0) {
    return (
      <div className="text-sm text-[#52525B] py-8 text-center">
        Nenhuma ocorrência registrada ainda. Faça o upload de um arquivo .TXT para começar.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div
          key={`${it.codigo}-${idx}`}
          className="grid grid-cols-12 items-center gap-3"
          data-testid={`stats-row-${idx}`}
        >
          <div className="col-span-2 text-xs font-mono-data font-semibold text-[#09090B]">
            {it.codigo || "—"}
          </div>
          <div className="col-span-7 text-sm text-[#52525B] truncate">
            {it.descricao || "Sem descrição"}
          </div>
          <div className="col-span-3 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-[#FAFAFA] rounded-sm overflow-hidden border border-[#E4E4E7]">
              <div
                className="h-full bg-[#09090B]"
                style={{ width: `${(it.total / max) * 100}%` }}
              />
            </div>
            <div className="text-xs font-mono-data font-semibold text-[#09090B] w-8 text-right">
              {it.total}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    getStats().then(setStats).catch(() => setStats(null));
  }, [refreshKey]);

  return (
    <div data-testid="page-dashboard" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Visão Geral
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Painel DocSS Logística
          </h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-xl">
            Monitoramento de ocorrências de transporte extraídas de arquivos
            EDI no padrão PROCEDA.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <KPICard
          testId="kpi-total-arquivos"
          label="Arquivos Processados"
          value={stats?.total_arquivos ?? "—"}
          sub="Acumulado total"
          icon={Files}
        />
        <KPICard
          testId="kpi-total-movs"
          label="Movimentações"
          value={stats?.total_movimentacoes ?? "—"}
          sub="NFs registradas"
          icon={Package}
        />
        <KPICard
          testId="kpi-entregas"
          label="Entregas Realizadas"
          value={stats?.total_entregas_realizadas ?? "—"}
          sub="Status de conclusão"
          accent="success"
          icon={CheckCircle2}
        />
        <KPICard
          testId="kpi-problemas"
          label="Problemas / Avarias"
          value={stats?.total_problemas ?? "—"}
          sub="Códigos 03-11 e 26"
          accent="error"
          icon={AlertTriangle}
        />
        <KPICard
          testId="kpi-em-atraso"
          label="Em Atraso"
          value={stats?.total_em_atraso ?? "—"}
          sub="Prazo vencido — tratar exceções"
          accent="warning"
          icon={Clock}
          onClick={() => navigate("/notas?atraso=1")}
        />
      </div>

      {/* Categorias (drill-down navega para Notas) */}
      <div className="mb-8">
        <CategoriasGrid
          selected={null}
          onSelect={(key) => {
            if (key) navigate(`/notas?categoria=${key}`);
            else navigate("/notas");
          }}
          refreshKey={refreshKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section
          className="bg-white border border-[#E4E4E7] rounded-md p-6"
          data-testid="dashboard-upload-section"
        >
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
              Importar EDI
            </div>
            <h2 className="font-heading text-xl font-semibold text-[#09090B] mt-1">
              Novo arquivo OCOREN
            </h2>
          </div>
          <UploadZone
            onUploaded={() => {
              setRefreshKey((k) => k + 1);
              setTimeout(() => navigate("/ocorrencias"), 700);
            }}
          />
        </section>

        <section
          className="bg-white border border-[#E4E4E7] rounded-md p-6"
          data-testid="dashboard-chart-section"
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
                Distribuição
              </div>
              <h2 className="font-heading text-xl font-semibold text-[#09090B] mt-1">
                Ocorrências por Tipo
              </h2>
            </div>
            <BarChart3 size={20} className="text-[#52525B]" strokeWidth={1.5} />
          </div>
          <StatsBar items={stats?.por_tipo || []} />
        </section>
      </div>

      {/* Gráfico temporal */}
      <div className="mt-6">
        <TimelineChart days={30} key={refreshKey} />
      </div>
    </div>
  );
}
